import React from 'react';
import {
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Box,
  Typography,
  Grid
} from '@mui/material';
import { Star } from '@mui/icons-material';

/**
 * Priority color mapping
 * 0: No Priority (Grey)
 * 1: Priority 1 - Highest (Red)
 * 2: Priority 2 (Orange)
 * 3: Priority 3 (Yellow)
 * 4: Priority 4 (Green)
 * 5: Priority 5 (Blue)
 */
export const PRIORITY_COLORS = {
  0: '#9e9e9e', // Grey
  1: '#f44336', // Red
  2: '#ff9800', // Orange
  3: '#ffeb3b', // Yellow
  4: '#4caf50', // Green
  5: '#2196f3', // Blue
};

export const PRIORITY_LABELS = {
  0: 'No Priority',
  1: 'Priority 1 (Highest)',
  2: 'Priority 2',
  3: 'Priority 3',
  4: 'Priority 4',
  5: 'Priority 5',
};

/**
 * FavoriteCheckbox Component
 * @param {boolean} isFavorite - Current favorite status
 * @param {function} onChange - Callback when checkbox changes
 * @param {string} label - Label text (default: "Mark as Favorite")
 */
export const FavoriteCheckbox = ({
  isFavorite = false,
  onChange,
  label = "Mark as Favorite"
}) => {
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="medium"
          checked={isFavorite}
          onChange={(e) => onChange(e.target.checked)}
          color="primary"
          icon={<Star sx={{ fontSize: 28, color: '#9e9e9e' }} />}
          checkedIcon={<Star sx={{ color: '#ffc107', fontSize: 28 }} />}
        />
      }
      label={
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
      }
    />
  );
};

/**
 * PriorityDropdown Component
 * @param {number} priority - Current priority level (0-5)
 * @param {boolean} enabled - Whether dropdown is enabled
 * @param {function} onChange - Callback when priority changes
 * @param {string} label - Label text (default: "Priority")
 */
export const PriorityDropdown = ({
  priority = 0,
  enabled = false,
  onChange,
  label = "Priority"
}) => {
  return (
    <TextField
      size="small"
      select
      fullWidth
      label={label}
      value={priority}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={!enabled}
      margin="dense"
      variant="outlined"
      sx={{
        opacity: enabled ? 1 : 0.5,
        backgroundColor: enabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
      }}
    >
      {Object.entries(PRIORITY_LABELS).map(([value, labelText]) => (
        <MenuItem key={value} value={Number(value)}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: PRIORITY_COLORS[Number(value)],
              }}
            />
            <Typography variant="body2">{labelText}</Typography>
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * FavoriteSection Component - Combines both checkbox and dropdown
 * @param {boolean} isFavorite - Current favorite status
 * @param {number} priority - Current priority level (0-5)
 * @param {function} onFavoriteChange - Callback when favorite status changes
 * @param {function} onPriorityChange - Callback when priority changes
 * @param {string} favoriteLabel - Label for checkbox
 * @param {string} priorityLabel - Label for dropdown
 */
export const FavoriteSection = ({
  isFavorite = false,
  priority = 0,
  onFavoriteChange,
  onPriorityChange,
  favoriteLabel = "Mark as Favorite",
  priorityLabel = "Priority",
}) => {
  const handleFavoriteChange = (checked) => {
    onFavoriteChange(checked);
    // Auto-reset priority to 0 when unchecking favorite
    if (!checked) {
      onPriorityChange(0);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FavoriteCheckbox
          isFavorite={isFavorite}
          onChange={handleFavoriteChange}
          label={favoriteLabel}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PriorityDropdown
          priority={priority}
          enabled={isFavorite}
          onChange={onPriorityChange}
          label={priorityLabel}
        />
      </Grid>
    </Grid>
  );
};

/**
 * PriorityIndicator Component - Shows a colored circle indicator
 * @param {number} priority - Priority level (0-5)
 * @param {number} size - Size of the indicator in pixels (default: 16)
 */
export const PriorityIndicator = ({ priority = 0, size = 16 }) => {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: PRIORITY_COLORS[priority] || PRIORITY_COLORS[0],
        display: 'inline-block',
      }}
      title={PRIORITY_LABELS[priority] || PRIORITY_LABELS[0]}
    />
  );
};

/**
 * FavoriteHelper - Utility functions for favorite feature
 */
export const FavoriteHelper = {
  getPriorityColor: (priority) => PRIORITY_COLORS[priority] || PRIORITY_COLORS[0],
  getPriorityLabel: (priority) => PRIORITY_LABELS[priority] || PRIORITY_LABELS[0],

  /**
   * Validates if a priority value is valid
   */
  isValidPriority: (priority) => {
    return priority >= 0 && priority <= 5;
  },

  /**
   * Sorts items by priority (ascending - highest priority first)
   */
  sortByPriority: (items, priorityField = 'priority') => {
    return [...items].sort((a, b) => {
      return (a[priorityField] || 0) - (b[priorityField] || 0);
    });
  },

  /**
   * Filters items that are marked as favorite
   */
  filterFavorites: (items, favoriteField = 'is_favorite') => {
    return items.filter(item => item[favoriteField]);
  },
};
